import { useEffect, useRef } from 'react';
import { colors } from '../constants/colors';
import { useReadOnlyChat } from '../hooks/useReadOnlyChat';
import { UserAvatar } from './UserAvatar';



interface ReadOnlyChatProps {
  channelId: string | null;
  sellerId?: string;
  maxHeight?: number;
  onSignInClick?: () => void;
}

export function ReadOnlyChat({ channelId, sellerId, maxHeight = 300, onSignInClick }: ReadOnlyChatProps) {
  const { messages, isConnected } = useReadOnlyChat(channelId);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!channelId) {
    return (
      <div style={styles.container}>
        <div style={styles.disconnected}>
          <p style={styles.disconnectedText}>Chat not available for this show</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header} className="chat-header">
        <span style={styles.headerText}>Live Chat</span>
      </div>

      <div style={{ ...styles.messagesContainer, maxHeight }}>
        {!isConnected ? (
          <p style={styles.emptyText}>Connecting to chat...</p>
        ) : messages.length === 0 ? (
          <p style={styles.emptyText}>No messages yet...</p>
        ) : (
          messages.map(msg => (
            <div key={msg.id} style={styles.message}>
              <UserAvatar src={msg.senderAvatar} alt={msg.senderName} size={28} />
              <div style={styles.messageContent}>
                <div style={styles.senderRow}>
                  <span style={styles.senderName}>{msg.senderName}</span>
                  {sellerId && msg.senderId === sellerId && (
                    <span style={styles.hostPill}>Host</span>
                  )}
                </div>
                <span style={styles.messageText}>{msg.content}</span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputContainer}>
        <div className="chat-input-pill" style={styles.inputPill}>
          <button
            onClick={onSignInClick}
            style={styles.signInPrompt}
          >
            Sign in to chat...
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.gray[300]}`,
    backgroundColor: '#f9fafb',
  },
  headerText: {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.ink.black,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
  },
  emptyText: {
    color: colors.gray[400],
    fontSize: '14px',
    textAlign: 'center',
    margin: '20px 0',
  },
  message: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '12px',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white,
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  avatarImg: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  messageContent: {
    flex: 1,
    minWidth: 0,
  },
  senderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '2px',
  },
  senderName: {
    fontWeight: '600',
    fontSize: '13px',
    color: colors.ink.black,
  },
  hostPill: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#fff',
    background: colors.accent.pink,
    padding: '1px 6px',
    borderRadius: 999,
    letterSpacing: '0.3px',
  },
  messageText: {
    display: 'block',
    fontSize: '14px',
    color: '#374151',
    wordBreak: 'break-word',
    lineHeight: 1.4,
  },
  inputContainer: {
    padding: '8px 12px',
    borderTop: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
  },
  inputPill: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '24px',
    padding: '4px 16px',
  },
  signInPrompt: {
    flex: 1,
    padding: '10px 0',
    fontSize: '14px',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: colors.gray[400],
    cursor: 'pointer',
    textAlign: 'left',
  },
  disconnected: {
    padding: '24px',
    textAlign: 'center',
  },
  disconnectedText: {
    color: colors.gray[400],
    fontSize: '14px',
  },
};
